import type { FormEvent } from 'react';
import clsx from 'clsx';
import { ToastManagerContext } from 'components/ToastManager';
import { useCreateCustomLinkMutation } from 'hooks/internalApi';
import { useContextSafely } from 'hooks/useContextSafely';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import React, { useCallback, useState } from 'react';
import { getResultOrError } from 'utils/errors';
import { useIK } from 'utils/ik';
import { useWorkspaceId } from 'wrappers/WorkspaceProvider';

import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { LinkIcon } from '@fragment/ui/src/components/Icon/LinkIcon/LinkIcon';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import { Toast } from '@fragment/ui/src/components/Toast';

import type { OnboardLinkFormProps } from './types';
import { DocsLink } from './DocsLink';

export const CreateCustomLinkForm = ({ onCreate }: OnboardLinkFormProps) => {
  const { showToast } = useContextSafely(ToastManagerContext);
  const workspaceId = useWorkspaceId();
  const [name, setName] = useState('');

  const { getIK, cycleIK } = useIK();
  const { context } = useInternalApiContext();
  const [, createCustomLink] = useCreateCustomLinkMutation();
  const handleCreate = useCallback(async () => {
    const { data, error } = await createCustomLink(
      {
        workspaceId,
        name,
        ik: getIK(),
      },
      context
    );
    const result = getResultOrError(data?.createCustomLink, error);
    if (result.isError) {
      showToast(<Toast message={result.errorMessage} type="error" />);
    } else {
      await onCreate(result.result.link);
      cycleIK();
    }
  }, [
    createCustomLink,
    workspaceId,
    name,
    getIK,
    context,
    onCreate,
    cycleIK,
    showToast,
  ]);
  const onSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      return handleCreate();
    },
    [handleCreate]
  );

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
    <form onSubmit={onSubmit} data-testid="custom-link-form">
      <div className={clsx('space-y-f2')}>
        <div className={clsx('flex flex-row justify-between')}>
          <div className="flex items-center space-x-[2px]">
            <LinkIcon type="CustomLink" />
            <span>Custom Link</span>
          </div>
          <DocsLink linkType="CustomLink" />
        </div>
        <div className={clsx('space-y-f2')}>
          <ModalField label="Name">
            <ModalTextInput
              // To keep backwards compatibility with the existing behavior
              placeholder="Link Name"
              onChange={(e) => setName(e.target.value)}
            />
          </ModalField>
          <PrimaryButtonV2 disabled={!name} type="submit">
            Add Link
          </PrimaryButtonV2>
        </div>
      </div>
    </form>
  );
};
